/**
 * The Calendar tab of Home's right rail: one day of the firm's reminders and
 * teammates' synced events on a compact time grid, with a Today / prev /
 * next toolbar, an Everyone / Me toggle, event popovers and a sync footer.
 */
import dayjs from "dayjs";
import {
  ArrowUpRight,
  Bell,
  CalendarRange,
  ChevronLeft,
  ChevronRight,
  ExternalLink
} from "lucide-react";
import { useCallback, useMemo, useState, type FC } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger
} from "@/components/ui/popover";
import { Skeleton } from "@/components/ui/skeleton";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { useTeamMembers } from "@/features/agents/useTeamMembers";
import {
  formatEventTime,
  TimeGridView,
  type CalendarEvent
} from "@/features/calendar";
import {
  rangeForView,
  useCalendarEvents,
  useFirmReminders,
  useMergedCalendar
} from "@/features/calendar/queries";
import { calendarPathFor } from "@/features/calendar/routes";
import { useCalendarConnection } from "@/features/calendar/useCalendarConnection";
import { CONNECTORS_PATH } from "@/features/connectors/routes";
import { relativeTime } from "@/features/agents/lib/time";
import { cn } from "@/lib/utils";

import { lawyerSelectors } from "../../../redux/lawyer/selectors";
import { ClientAvatar } from "./ClientAvatar";

export const RAIL_SCROLL_TO_HOUR = 8.5;

type Who = "everyone" | "me";

const PROVIDER_LABEL: Record<CalendarEvent["source"], string> = {
  reminder: "CaseBlink",
  google: "Google Calendar",
  outlook: "Outlook"
};

/** Google "G"/Outlook "O" marks for the footer. */
export const SourceMark: FC<{
  source: "google" | "outlook";
  className?: string;
}> = ({ source, className }) => (
  <span
    className={cn(
      "inline-flex size-3.5 items-center justify-center rounded-xs border border-border text-xs font-semibold leading-none text-muted-foreground",
      className
    )}
    title={PROVIDER_LABEL[source]}
    data-testid={`source-mark-${source}`}
  >
    {source === "google" ? "G" : "O"}
  </span>
);

const syncedLabel = (
  syncedAt: string | null,
  calendars: number,
  now: number
): string => {
  if (calendars === 0) return "No calendars synced";
  const rel = relativeTime(syncedAt, now);
  const when = rel ? (rel === "now" ? "just now" : `${rel} ago`) : "";
  return `Synced ${when} from ${calendars} calendar${
    calendars === 1 ? "" : "s"
  }`.replace("  ", " ");
};

type PopoverProps = {
  event: CalendarEvent | null;
  onOpenChange: (open: boolean) => void;
  nameOf: (uid: string) => string | undefined;
  onOpenCalendar: (event: CalendarEvent) => void;
};

/** Details for any rail event; synced ones link out to their provider. */
const RailEventPopover: FC<PopoverProps> = ({
  event,
  onOpenChange,
  nameOf,
  onOpenCalendar
}) => {
  const synced = event ? event.source !== "reminder" : false;
  const owner = event?.ownerUid ? nameOf(event.ownerUid) : undefined;
  return (
    <Popover open={event !== null} onOpenChange={onOpenChange}>
      <PopoverTrigger asChild>
        <span className="absolute top-12 left-2.5 size-0" aria-hidden />
      </PopoverTrigger>
      {event ? (
        <PopoverContent
          align="start"
          side="bottom"
          className="w-75 p-3"
          data-testid="rail-event-popover"
        >
          <div className="flex items-start gap-2">
            <span className="mt-0.5 text-muted-foreground">
              {synced ? (
                <CalendarRange className="size-4" />
              ) : (
                <Bell className="size-4" />
              )}
            </span>
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium text-foreground">
                {event.title}
              </div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                {dayjs(event.start).format("ddd, MMM D")} ·{" "}
                {formatEventTime(event)}
              </div>
              {event.clientName ? (
                <Link
                  to={`/clients/${encodeURIComponent(event.clientId ?? "")}`}
                  className="mt-1.5 inline-flex items-center gap-1.5 text-xs text-foreground hover:underline"
                >
                  <ClientAvatar name={event.clientName} size={14} />
                  {event.clientName}
                </Link>
              ) : null}
              <div className="mt-1.5 text-xs text-muted-foreground">
                {synced
                  ? `${owner ?? "A teammate"}'s ${PROVIDER_LABEL[event.source]}`
                  : event.location || null}
              </div>
            </div>
          </div>
          <div className="mt-3 flex items-center justify-end gap-2">
            {synced && event.externalUrl ? (
              <Button asChild size="sm" variant="outline">
                <a href={event.externalUrl} target="_blank" rel="noreferrer">
                  <ExternalLink className="size-3.5" />
                  Open in {PROVIDER_LABEL[event.source]}
                </a>
              </Button>
            ) : null}
            <Button
              variant="primary"
              size="sm"
              onClick={() => onOpenCalendar(event)}
            >
              Open in Calendar
            </Button>
          </div>
        </PopoverContent>
      ) : null}
    </Popover>
  );
};

type Props = {
  className?: string;
};

export const CalendarRailTab: FC<Props> = ({ className }) => {
  const navigate = useNavigate();
  const uid = useSelector(lawyerSelectors.selectUid) as string | null;
  const { members } = useTeamMembers();
  const [anchor, setAnchor] = useState(() => new Date());
  const [who, setWho] = useState<Who>("everyone");
  const [popover, setPopover] = useState<CalendarEvent | null>(null);

  const range = useMemo(() => rangeForView("day", anchor), [anchor]);
  const reminders = useFirmReminders(range, {
    assignee: who === "me" ? uid : null
  });
  const synced = useCalendarEvents(range);
  const connection = useCalendarConnection();

  const external = useMemo(
    () =>
      who === "me"
        ? synced.events.filter((e) => e.ownerUid === uid)
        : synced.events,
    [synced.events, who, uid]
  );
  const events = useMergedCalendar(reminders.events, external);

  const nameOf = useCallback(
    (id: string) => members.find((m) => m.uid === id)?.label,
    [members]
  );
  const openCalendar = useCallback(
    (event?: CalendarEvent) =>
      navigate(calendarPathFor(event ? event.start : anchor, "day")),
    [anchor, navigate]
  );

  const calendars = synced.sources.filter((s) => s.status === "ok").length;
  const providers = Array.from(new Set(synced.sources.map((s) => s.provider)));
  const isLoading = reminders.isLoading && synced.isLoading;
  const showConnect =
    !connection.isLoading && connection.enabled && !connection.connected;

  return (
    <div
      className={cn("relative flex min-h-0 flex-1 flex-col", className)}
      data-testid="calendar-rail-tab"
    >
      <div className="flex h-11 shrink-0 items-center gap-1 py-0 pr-2.5 pl-3">
        <Button
          size="xs"
          variant="outline"
          onClick={() => setAnchor(new Date())}
          data-testid="rail-today"
        >
          Today
        </Button>
        <Button
          size="icon-xs"
          variant="ghost"
          aria-label="Previous day"
          onClick={() => setAnchor((a) => dayjs(a).subtract(1, "day").toDate())}
        >
          <ChevronLeft className="size-3.5" />
        </Button>
        <Button
          size="icon-xs"
          variant="ghost"
          aria-label="Next day"
          onClick={() => setAnchor((a) => dayjs(a).add(1, "day").toDate())}
        >
          <ChevronRight className="size-3.5" />
        </Button>
        <span
          className="ml-0.5 text-base font-semibold text-foreground"
          data-testid="rail-day-label"
        >
          {dayjs(anchor).format("ddd, MMM D")}
        </span>
        <span className="flex-1" />
        <ToggleGroup
          type="single"
          size="sm"
          variant="outline"
          value={who}
          onValueChange={(v) => v && setWho(v as Who)}
          aria-label="Whose calendar"
          className="h-6"
        >
          <ToggleGroupItem value="everyone" className="h-6 px-2 text-xs">
            Everyone
          </ToggleGroupItem>
          <ToggleGroupItem value="me" className="h-6 px-2 text-xs">
            Me
          </ToggleGroupItem>
        </ToggleGroup>
      </div>

      {isLoading ? (
        <div
          className="flex flex-col gap-2.5 pt-2 pr-3 pl-14"
          data-testid="calendar-rail-loading"
        >
          {[38, 56, 28, 70, 40].map((h, i) => (
            <Skeleton
              key={i}
              className="w-full rounded-lg"
              style={{ height: h }}
            />
          ))}
        </div>
      ) : (
        <div className="flex min-h-0 flex-1 flex-col border-t border-border pr-2">
          <TimeGridView
            mode="day"
            anchor={anchor}
            events={events}
            highlightedId={popover?.id ?? null}
            onOpenEvent={setPopover}
            variant="rail"
            scrollToHour={RAIL_SCROLL_TO_HOUR}
          />
        </div>
      )}

      <footer className="flex h-8 shrink-0 items-center gap-1.5 border-t border-border py-0 pr-1.5 pl-3 text-xs text-muted-foreground">
        {showConnect ? (
          <Link
            to={CONNECTORS_PATH}
            className="flex-1 truncate text-foreground hover:underline"
            data-testid="rail-connect-calendar"
          >
            Connect Google or Outlook to see your calendar here
          </Link>
        ) : (
          <>
            {providers.map((p) => (
              <SourceMark key={p} source={p} />
            ))}
            <span className="flex-1 truncate" data-testid="rail-synced-label">
              {syncedLabel(synced.syncedAt, calendars, Date.now())}
            </span>
          </>
        )}
        <Button
          size="xs"
          variant="ghost"
          className="text-xs font-medium text-foreground"
          onClick={() => openCalendar()}
          data-testid="rail-open-calendar"
        >
          Open calendar
          <ArrowUpRight className="size-3 text-muted-foreground" />
        </Button>
      </footer>

      <RailEventPopover
        event={popover}
        onOpenChange={(open) => {
          if (!open) setPopover(null);
        }}
        nameOf={nameOf}
        onOpenCalendar={(e) => {
          setPopover(null);
          openCalendar(e);
        }}
      />
    </div>
  );
};
